//import { format } from 'date-fns';
import { openCalendarInvite } from '../utils/calendarInvite.js';
import { CalendarIcon, PlusIcon } from '@heroicons/react/24/outline';
import { saveEventToUser } from '../utils/eventActions.js';
import { userSession } from '../utils/userSession.js';
import { useState } from 'react';
import StatusOverlay from './StatusOverlay.js';
import { trackEvent, EventTypes } from '../utils/analytics.js';

export default function EventTableView({ events, onOpenLogin, onEventAdded }) {
  const [statusMessage, setStatusMessage] = useState('');
  const [showStatus, setShowStatus] = useState(false);
  const [statusType, setStatusType] = useState('success');

  const showStatusMessage = (message, type = 'success') => {
    setStatusMessage(message);
    setStatusType(type);
    setShowStatus(true);
    setTimeout(() => {
      setShowStatus(false);
      setStatusMessage('');
      setStatusType('success');
    }, 2000);
  };

  const handleAddEvent = async (eventId) => {
    if (!userSession.isLoggedIn()) {
      onOpenLogin("Sign in to add events to DriftCal");
      return;
    }

    try {
      const result = await saveEventToUser(null, eventId);
      showStatusMessage(
        result.alreadySaved ? 'Event already saved' : 'Event added',
        result.alreadySaved ? 'warning' : 'success'
      );
      // Notify parent that an event was added
      if (!result.alreadySaved) {
        onEventAdded();
      }
    } catch (error) {
      console.error('Error in handleAddEvent:', {
        error,
        errorMessage: error.message,
        errorStack: error.stack,
        eventId,
        userSession: {
          isLoggedIn: userSession.isLoggedIn(),
          hasUser: !!userSession.getUser(),
          userId: userSession.getUserId()
        }
      });
      showStatusMessage('Failed to add event', 'error');
    }
  };

  const getEventTypeStyles = (eventType) => {
    switch(eventType) {
      case 'Beginner':
        return 'bg-blue-300 text-blue-800';
      case 'Practice':
        return 'bg-green-200 text-green-800';
      case 'Competition':
        return 'bg-orange-200 text-orange-800';
      case 'Exhibition':
        return 'bg-red-200 text-red-800';
      default:
        return 'bg-gray-200 text-gray-800';
    }
  };

  const formatDateTime = (dateString, currentEvent) => {
    try {
      const [datePart, timePart] = dateString.split('T');
      const [year, month, day] = datePart.split('-');
      const [hours, minutes] = timePart.split(':');

      // Convert to 12-hour format
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'pm' : 'am';
      const hour12 = hour % 12 || 12;

      // Convert month number to abbreviated name
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[parseInt(month) - 1];

      // Check if we only need to show time
      if (dateString === currentEvent.EndDateTime && 
          isSameDay(currentEvent.StartDateTime, currentEvent.EndDateTime)) {
        return `${hour12}:${minutes.padStart(2, '0')} ${ampm}`;
      }

      // For start date or different days
      return `${day}-${monthName}-${year.slice(-2)}, ${hour12}:${minutes.padStart(2, '0')} ${ampm}`;
    } catch (error) {
      return dateString;
    }
  };

  const isSameDay = (date1, date2) => {
    return date1.split('T')[0] === date2.split('T')[0];
  };

  const handleCalendarInvite = (event) => {
    // Track the calendar download event
    trackEvent(EventTypes.ICAL_DOWNLOAD, {
      event_id: event.EventID,
      event_name: event.EventName,
      promoter_id: event.OrganiserID,
      promoter_name: event.BusinessName
    });
    
    // Open the calendar invite
    openCalendarInvite(event);
  };

  const handleEventNameClick = (event) => {
    trackEvent(EventTypes.EVENT_CLICK, {
      event_id: event.EventID,
      event_name: event.EventName,
      promoter_id: event.OrganiserID,
      promoter_name: event.BusinessName,
      view_type: 'table'
    });
  };

  const getStateStyles = (stateCode) => {
    switch(stateCode) {
      case 'VIC':
        return 'bg-gray-500';
      case 'NSW':
        return 'bg-sky-500';
      case 'ACT':
        return 'bg-blue-500';
      case 'QLD':
        return 'bg-rose-800';
      case 'SA':
        return 'bg-red-600';
      case 'NT':
        return 'bg-orange-700';
      case 'WA':
        return 'bg-yellow-500';
      case 'TAS':
        return 'bg-green-600';
      default:
        return 'bg-slate-900';
    }
  };

  return (
    <div className="relative">
      {/* Status Overlay */}
      {showStatus && (
        <StatusOverlay 
          message={statusMessage} 
          type={statusType}
        />
      )}

      <div className="flow-root h-[calc(100vh-220px)] ">
        <div className="h-full overflow-auto rounded-2xl scrollbar-thin scrollbar-thumb-slate-200 hover:scrollbar-thumb-slate-700 scrollbar-track-transparent">
        {/* Desktop Table */}
        <table className="min-w-full divide-y divide-gray-400 hidden md:table" >
        
          <thead className="sticky top-0 bg-slate-900">
            <tr>
              <th className="px-2 py-2 text-center text-xs font-medium text-white uppercase tracking-wider">driftCal</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">ICS</th>
              <th className="px-1 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">State</th>
              <th className="px-1 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Promoter</th>
              <th className="px-1 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Event Name</th>
              <th className="px-1 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Type</th>
              <th className="px-1 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Start</th>
              <th className="px-1 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">End</th>
              <th className="px-1 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Venue</th>
              <th className="px-1 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Layout</th>

            </tr>
          </thead>
          <tbody className="bg-slate-100 divide-y divide-gray-200">
            {events.map((event, index) => (
              <tr 
                key={`${event.BusinessName}-${event.StartDateTime}-${index}`} 
                className="hover:bg-gray-50"
              >
                <td className="px-1 py-1 text-xs text-center">
                  <button
                    onClick={() => handleAddEvent(event.EventID)}
                    className="inline-flex items-center justify-center rounded-md p-2 text-slate-900 hover:text-gray-800"
                    title="Add to my driftCal"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </td>
                <td className="px-1 py-1 text-xs">
                  <button
                    onClick={() => handleCalendarInvite(event)}
                    className="rounded-md p-2 text-slate-900 hover:text-gray-800"
                    title="Add to Calendar"
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </button>
                </td>
                <td className="px-1 py-1 text-xs">
                  <span className={`inline-flex items-center justify-center w-10 px-2 py-0.5 rounded-full text-xs font-medium ${getStateStyles(event.StateNameShort)} text-white`}>
                    {event.StateNameShort}
                  </span>
                </td>
                <td className="px-1 py-1 text-xs">{event.BusinessName}</td>
                <td className="px-1 py-1 text-xs underline hover:no-underline">
                  <a 
                    href={event.EventLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => handleEventNameClick(event)}
                  >
                    {event.EventType === 'Competition' && event.RoundNum
                      ? `${event.EventName} - Round ${event.RoundNum}`
                      : event.EventName}
                  </a>
                </td>
                <td className="px-1 py-1 text-xs">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getEventTypeStyles(event.EventType)}`}>
                    {event.EventType}
                  </span>
                </td>
                <td className="px-1 py-1 text-xs">
                  {formatDateTime(event.StartDateTime, event)}
                </td>
                <td className="px-1 py-1 text-xs">
                  {formatDateTime(event.EndDateTime, event)}
                </td>
                <td className="px-1 py-1 text-xs underline hover:no-underline">
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.VenueName)}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {event.VenueName}
                  </a>
                </td>
                <td className="px-1 py-1 text-xs">{event.LayoutName}</td>
              </tr>
            ))}
          </tbody>
          
        </table>
        </div>
      </div>
    </div>
    
  );
}
